// src/pages/ParticipantProfilePage.js (User App)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../apiClient';
import { useParticipantAuth } from '../context/ParticipantAuthContext';
import { QRCodeCanvas } from 'qrcode.react';
import { PeraWalletConnect } from '@perawallet/connect';
import algosdk from 'algosdk';
import AppleWalletButton from '../components/AppleWalletButton';
import GoogleWalletButton from '../components/GoogleWalletButton';
import '../styles/ParticipantProfilePage.css';

const peraWallet = new PeraWalletConnect();

// Feature flag: hide the "Take ownership" flow for now. Keep the code (handlers, state)
// in place so we can re-enable by flipping this to true once the UX is finalized.
const TAKE_OWNERSHIP_ENABLED = false;

const formatRewardCost = (cost, isOnChain, asaDecimals, asaUnitName) => {
    if (isOnChain) {
        const dec = asaDecimals || 0;
        const unit = asaUnitName || 'tokens';
        return `${Number(cost).toLocaleString(undefined, { maximumFractionDigits: dec })} ${unit}`;
    }
    return `${Math.round(cost)} pts`;
};

const formatAsaAmount = (baseUnits, decimals) => {
    if (!decimals) return String(baseUnits || 0);
    const factor = Math.pow(10, decimals);
    const value = (Number(baseUnits || 0) / factor);
    // Trim trailing zeros but keep up to `decimals` precision
    return value.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const ParticipantProfilePage = () => {
    const { isAuthenticated, participantToken, programId, logout } = useParticipantAuth();
    const navigate = useNavigate();

    const [profileData, setProfileData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // Send-tokens state
    const [sendOpen, setSendOpen] = useState(false);
    const [sendAck, setSendAck] = useState(false); // user has acknowledged the warning
    const [sendDest, setSendDest] = useState('');
    const [sendAmount, setSendAmount] = useState('');
    const [sendBusy, setSendBusy] = useState(false);
    const [sendMsg, setSendMsg] = useState('');

    // Take-ownership state
    const [ownStep, setOwnStep] = useState(0); // 0=closed, 1=warning, 2=mnemonic shown, 3=confirm wipe
    const [ownMnemonic, setOwnMnemonic] = useState('');
    const [ownAddress, setOwnAddress] = useState('');
    const [ownBusy, setOwnBusy] = useState(false);
    const [ownMsg, setOwnMsg] = useState('');

    // Rewards state
    const [rewards, setRewards] = useState([]);
    const [rewardsLoading, setRewardsLoading] = useState(false);
    const [redeemingId, setRedeemingId] = useState(null);
    const [rewardMsg, setRewardMsg] = useState('');
    const [myRedemptions, setMyRedemptions] = useState([]);

    const fetchProfile = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await apiClient.get(`/me/programs/${programId}/profile`);
            setProfileData(response.data);
        } catch (err) {
            console.error("Error fetching participant profile:", err);
            const errorMsg = err.response?.data?.detail || 'Failed to load profile.';
            if (err.response?.status === 401 || err.response?.status === 403) {
                setError('Session expired or invalid. Please log in again.');
                logout();
                navigate('/');
            } else {
                setError(errorMsg);
            }
        } finally {
            setIsLoading(false);
        }
    }, [programId, logout, navigate]);

    const fetchRewards = useCallback(async () => {
        if (!programId) return;
        setRewardsLoading(true);
        try {
            const res = await apiClient.get(`/me/programs/${programId}/rewards`);
            setRewards(res.data || []);
        } catch (err) {
            console.warn('Failed to load rewards:', err);
            setRewards([]);
        } finally {
            setRewardsLoading(false);
        }
    }, [programId]);

    useEffect(() => {
        if (!isAuthenticated || !participantToken) { navigate('/'); return; }
        if (!programId) { setError('Program context is missing. Please log in again.'); setIsLoading(false); return; }
        fetchProfile();
        fetchRewards();
    }, [isAuthenticated, participantToken, programId, navigate, fetchProfile, fetchRewards]);

    const handleLogout = () => { logout(); navigate('/'); };

    const isOnChain = profileData?.program_asset_type === 'points_on_chain';
    const walletAddress = profileData?.pera_address_linked
        ? null /* Pera users use their own wallet — server doesn't return the address through the linked-bool projection, falls through to algorand_address */
        : profileData?.custodial_address || profileData?.algorand_address;
    const displayAddress = walletAddress || profileData?.algorand_address;
    const isCustodial = isOnChain && !!profileData?.custodial_address && !profileData?.pera_address_linked;

    const pointsLabel = isOnChain
        ? `${formatAsaAmount(profileData?.points_balance, profileData?.program_asa_decimals || 0)} ${profileData?.program_asa_unit_name || ''}`.trim()
        : (profileData?.points_balance ?? 0);

    const handleSend = async (e) => {
        e.preventDefault();
        setSendMsg('');
        if (!sendAck) { setSendMsg('Please acknowledge the warning first.'); return; }
        const amount = parseFloat(sendAmount);
        if (!sendDest || !(amount > 0)) { setSendMsg('Enter a destination address and a positive amount.'); return; }
        setSendBusy(true);
        try {
            const res = await apiClient.post(`/me/programs/${programId}/wallet/withdraw`, {
                destination_address: sendDest.trim(),
                amount_display: amount,
            });
            setSendMsg(`Sent! txid ${res.data.txid.slice(0, 12)}…`);
            setSendDest(''); setSendAmount(''); setSendAck(false);
            await fetchProfile();
        } catch (err) {
            const detail = err.response?.data?.detail || err.message || 'Send failed.';
            setSendMsg(`Error: ${detail}`);
        } finally {
            setSendBusy(false);
        }
    };

    const handleRevealSeed = async () => {
        setOwnMsg(''); setOwnBusy(true);
        try {
            const res = await apiClient.post(`/me/programs/${programId}/wallet/reveal-seed`);
            setOwnMnemonic(res.data.mnemonic);
            setOwnAddress(res.data.address);
            setOwnStep(2);
        } catch (err) {
            setOwnMsg(`Error: ${err.response?.data?.detail || err.message || 'Failed to reveal mnemonic.'}`);
        } finally {
            setOwnBusy(false);
        }
    };

    const handleRedeem = async (reward) => {
        const isOnChainProgram = profileData?.program_asset_type === 'points_on_chain';
        const usesPera = isOnChainProgram && !!profileData?.pera_address_linked;
        const costLabel = formatRewardCost(
            reward.cost, isOnChainProgram,
            profileData?.program_asa_decimals, profileData?.program_asa_unit_name,
        );
        if (!window.confirm(`Redeem "${reward.title}" for ${costLabel}?`)) return;

        setRewardMsg(''); setRedeemingId(reward.id);
        try {
            if (usesPera) {
                // Pera-signin → backend builds unsigned txn; user signs with Pera; backend submits.
                const prep = await apiClient.post(`/me/programs/${programId}/rewards/${reward.id}/redeem/prepare`);
                const { unsigned_txn_b64 } = prep.data;
                let address;
                try {
                    const reconnect = await peraWallet.reconnectSession();
                    if (reconnect && reconnect.length) address = reconnect[0];
                } catch { /* ignore */ }
                if (!address) {
                    try { await peraWallet.disconnect(); } catch { /* nothing to disconnect */ }
                    const accounts = await peraWallet.connect();
                    address = accounts[0];
                }
                if (!address) throw new Error('Pera Wallet not connected.');
                const bytes = Uint8Array.from(atob(unsigned_txn_b64), (c) => c.charCodeAt(0));
                const txn = algosdk.decodeUnsignedTransaction(bytes);
                const signedTxns = await peraWallet.signTransaction([[{ txn, signers: [address] }]]);
                const signed_b64 = btoa(String.fromCharCode(...signedTxns[0]));
                const sub = await apiClient.post(`/me/programs/${programId}/rewards/${reward.id}/redeem/submit`, {
                    reward_id: reward.id, signed_txn_b64: signed_b64,
                });
                setMyRedemptions(prev => [sub.data, ...prev]);
                setRewardMsg(`Redeemed "${reward.title}". Show the admin to fulfill.`);
            } else {
                // Off-chain or custodial → single-call redemption.
                const res = await apiClient.post(`/me/programs/${programId}/rewards/${reward.id}/redeem`);
                setMyRedemptions(prev => [res.data, ...prev]);
                setRewardMsg(`Redeemed "${reward.title}". Show the admin to fulfill.`);
            }
            await fetchProfile();
        } catch (err) {
            const detail = err.response?.data?.detail || err.message || 'Redemption failed.';
            setRewardMsg(`Error: ${detail}`);
        } finally {
            setRedeemingId(null);
        }
    };

    const handleFinalizeOwnership = async () => {
        setOwnMsg(''); setOwnBusy(true);
        try {
            await apiClient.post(`/me/programs/${programId}/wallet/take-ownership`, { confirm: true });
            setOwnStep(0);
            setOwnMnemonic(''); setOwnAddress('');
            await fetchProfile();
        } catch (err) {
            setOwnMsg(`Error: ${err.response?.data?.detail || err.message || 'Failed to take ownership.'}`);
        } finally {
            setOwnBusy(false);
        }
    };

    if (isLoading) return <div className="profile-container loading-container"><p>Loading profile...</p></div>;
    if (error) return (
        <div className="profile-container error-container">
            <h2>Error</h2><p>{error}</p>
            <button onClick={() => navigate('/')}>Go to Homepage</button>
        </div>
    );
    if (!profileData) return <div className="profile-container"><p>No profile data available.</p></div>;

    return (
        <div className="profile-container">
            <header className="profile-header">
                <div className="profile-header-title">
                    {(() => {
                        const raw = profileData.program_logo_url;
                        if (!raw) return null;
                        let src = raw;
                        if (!raw.startsWith('http://') && !raw.startsWith('https://')) {
                            const base = apiClient.defaults.baseURL.endsWith('/')
                                ? apiClient.defaults.baseURL.slice(0, -1)
                                : apiClient.defaults.baseURL;
                            const path = raw.startsWith('/') ? raw : `/${raw}`;
                            src = `${base}${path}`;
                        }
                        return <img src={src} alt={`${profileData.program_name} logo`} className="profile-program-logo" />;
                    })()}
                    <h1>{profileData.program_name}</h1>
                </div>
                <p>Welcome, {profileData.username || profileData.user_identifier}!</p>
            </header>

            <section className="account-section">
                <h2>Account</h2>
                <div className="account-fields">
                    <div className="account-field">
                        <label>Email</label>
                        <div className="account-value-row">
                            {profileData.email
                                ? <span>{profileData.email}</span>
                                : <span className="account-muted">No email on file</span>}
                            {profileData.email && (profileData.is_confirmed
                                ? <span className="account-badge account-badge-ok">Confirmed</span>
                                : <span className="account-badge account-badge-warn">Not confirmed</span>)}
                            {profileData.google_id_linked && <span className="account-badge account-badge-info">Google</span>}
                            {profileData.pera_address_linked && <span className="account-badge account-badge-info">Pera Wallet</span>}
                        </div>
                    </div>

                    {profileData.username && (
                        <div className="account-field">
                            <label>Username</label>
                            <div className="account-value-row"><span>{profileData.username}</span></div>
                        </div>
                    )}

                    <div className="account-field">
                        <label>User ID</label>
                        <div className="account-value-row">
                            <code className="account-mono">{profileData.user_identifier}</code>
                        </div>
                    </div>

                    {displayAddress && (
                        <div className="account-field">
                            <label>
                                {isCustodial ? 'Custodial wallet address'
                                    : profileData.pera_address_linked ? 'Linked Pera wallet'
                                    : 'Wallet address'}
                            </label>
                            <div className="account-value-row">
                                <code className="account-mono account-address">{displayAddress}</code>
                                <button
                                    type="button"
                                    className="account-link-btn"
                                    onClick={() => navigator.clipboard?.writeText(displayAddress)}
                                >
                                    Copy
                                </button>
                            </div>
                            {isCustodial && (
                                <small className="account-help">
                                    Temporary custodial wallet (prefunded ~0.25 ALGO). See <em>Send tokens</em> /{' '}
                                    <em>Take ownership</em> below to move out.
                                </small>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <section className="profile-details">
                <div className="points-balance-card">
                    <h2>{isOnChain ? 'Your Balance' : 'Your Points'}</h2>
                    <p className="points-value">{pointsLabel}</p>
                </div>

                {/* Always render a QR so off-chain email participants (who have no
                    algorand_address and no custodial_address) still get something the
                    admin can scan to identify them. Falls back to user_identifier — the
                    admin scanner + backend lookup accept any participant identifier. */}
                {(displayAddress || profileData.user_identifier) && (
                    <div className="qr-code-card">
                        <h2>{displayAddress ? 'Your Wallet QR' : 'Your Participant QR'}</h2>
                        <div className="qr-code-wrapper">
                            <QRCodeCanvas
                                value={displayAddress || profileData.user_identifier}
                                size={200} level={"H"} includeMargin={true}
                            />
                        </div>
                        <p className="algo-address-display">
                            {displayAddress ? 'ID' : 'Member'}:{' '}
                            <span>{displayAddress || profileData.user_identifier}</span>
                        </p>
                    </div>
                )}
            </section>

            <section className="rewards-section">
                <h2>Available rewards</h2>
                {rewardsLoading && <p>Loading rewards…</p>}
                {!rewardsLoading && rewards.length === 0 && (
                    <p className="info-message">No rewards available right now. Check back soon.</p>
                )}
                {!rewardsLoading && rewards.length > 0 && (
                    <div className="reward-list">
                        {rewards.map(r => {
                            const imgSrc = r.image_url
                                ? (r.image_url.startsWith('http') ? r.image_url : `${apiClient.defaults.baseURL}${r.image_url}`)
                                : null;
                            const costLabel = formatRewardCost(
                                r.cost, isOnChain,
                                profileData?.program_asa_decimals,
                                profileData?.program_asa_unit_name,
                            );
                            return (
                                <div key={r.id} className="reward-card">
                                    <div className="reward-card-image">
                                        {imgSrc ? <img src={imgSrc} alt={r.title} /> : <div className="reward-image-placeholder">No image</div>}
                                    </div>
                                    <div className="reward-card-body">
                                        <h3>{r.title}</h3>
                                        {r.description && <p className="reward-desc">{r.description}</p>}
                                        <p className="reward-cost">{costLabel}</p>
                                        <button
                                            onClick={() => handleRedeem(r)}
                                            disabled={redeemingId === r.id}
                                            className="redeem-btn"
                                        >
                                            {redeemingId === r.id ? 'Redeeming…' : 'Redeem'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {rewardMsg && <p className={rewardMsg.startsWith('Error') ? 'error-message' : 'success-message'}>{rewardMsg}</p>}
                {myRedemptions.length > 0 && (
                    <div className="my-redemptions">
                        <h3>Your recent redemptions</h3>
                        <ul>
                            {myRedemptions.map(red => (
                                <li key={red.id}>
                                    <strong>{red.reward_title || `Reward #${red.reward_id}`}</strong>
                                    {' — '}<span className={`status-${red.status}`}>{red.status}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>

            {isCustodial && (
                <section className="custodial-wallet-section">
                    <h2>Send tokens</h2>
                    <p className="info-message">
                        This wallet is for <strong>temporary storage</strong>. We strongly recommend
                        creating your own wallet (Pera, Defly, …) and sending your tokens there.
                    </p>
                    {!sendOpen ? (
                        <button onClick={() => { setSendOpen(true); setSendMsg(''); }}>Send tokens to another address</button>
                    ) : (
                        <form onSubmit={handleSend} className="send-tokens-form">
                            <label>
                                <input type="checkbox" checked={sendAck} onChange={(e) => setSendAck(e.target.checked)} disabled={sendBusy} />
                                I understand transfers are irreversible and that this custodial wallet is temporary.
                            </label>
                            <input
                                type="text" placeholder="Destination Algorand address (58 chars)"
                                value={sendDest} onChange={(e) => setSendDest(e.target.value)} disabled={sendBusy}
                            />
                            <input
                                type="text" placeholder={`Amount${profileData.program_asa_unit_name ? ` (${profileData.program_asa_unit_name})` : ''}`}
                                value={sendAmount} onChange={(e) => setSendAmount(e.target.value.replace(/[^0-9.]/g, ''))} disabled={sendBusy}
                            />
                            <div className="send-actions">
                                <button type="submit" disabled={sendBusy || !sendAck || !sendDest || !sendAmount}>
                                    {sendBusy ? 'Sending…' : 'Send'}
                                </button>
                                <button type="button" onClick={() => { setSendOpen(false); setSendMsg(''); }} disabled={sendBusy}>
                                    Cancel
                                </button>
                            </div>
                            {sendMsg && <p className={sendMsg.startsWith('Error') ? 'error-message' : 'success-message'}>{sendMsg}</p>}
                        </form>
                    )}

                    {TAKE_OWNERSHIP_ENABLED && <>
                    <h2>Take ownership</h2>
                    <p className="info-message">
                        Reveal the 25-word mnemonic for this wallet, save it somewhere safe, then ask
                        ZappyRewards to wipe the encrypted copy from the server. After that, only you
                        can sign transactions for this address.
                    </p>
                    {ownStep === 0 && (
                        <button onClick={() => setOwnStep(1)} disabled={ownBusy}>Start: take ownership</button>
                    )}
                    {ownStep === 1 && (
                        <div className="ownership-step">
                            <p>
                                <strong>Important:</strong> after revealing, write down the 25 words and store them
                                offline. ZappyRewards will not be able to recover this wallet.
                            </p>
                            <button onClick={handleRevealSeed} disabled={ownBusy}>
                                {ownBusy ? 'Revealing…' : 'Reveal 25-word mnemonic'}
                            </button>
                            <button onClick={() => setOwnStep(0)} disabled={ownBusy}>Cancel</button>
                            {ownMsg && <p className="error-message">{ownMsg}</p>}
                        </div>
                    )}
                    {ownStep === 2 && (
                        <div className="ownership-step">
                            <p><strong>Mnemonic for {ownAddress}:</strong></p>
                            <pre className="mnemonic-display">{ownMnemonic}</pre>
                            <p>Once you have safely stored these 25 words, click <em>I’ve saved it</em> below.</p>
                            <button onClick={() => setOwnStep(3)}>I’ve saved it — continue</button>
                            <button onClick={() => { setOwnStep(0); setOwnMnemonic(''); }}>Cancel</button>
                        </div>
                    )}
                    {ownStep === 3 && (
                        <div className="ownership-step">
                            <p>
                                Final step: confirm ZappyRewards should wipe the encrypted seed. After this,
                                you cannot withdraw via this app — sign with your own wallet using the mnemonic you saved.
                            </p>
                            <button onClick={handleFinalizeOwnership} disabled={ownBusy}>
                                {ownBusy ? 'Wiping…' : 'Wipe seed from server'}
                            </button>
                            <button onClick={() => setOwnStep(0)} disabled={ownBusy}>Cancel</button>
                            {ownMsg && <p className="error-message">{ownMsg}</p>}
                        </div>
                    )}
                    </>}
                </section>
            )}

            <section className="profile-actions">
                <div className="wallet-actions">
                    <AppleWalletButton />
                    <GoogleWalletButton />
                </div>
                <button onClick={handleLogout} className="logout-button">Logout</button>
            </section>
        </div>
    );
};

export default ParticipantProfilePage;
